/**
 * Turn a completed questionnaire into an Agents Alliance branded PDF for an
 * underwriter.
 *
 * This is what the whole form exists to produce. An underwriter will not read a
 * JSON blob or a screenshot, and the carrier's own PDF can't be filled from our
 * answers without a field-mapping exercise per carrier. A clean AAS document
 * carrying the same questions and the answers is what actually gets read.
 *
 * Built on jsPDF and the same navy/orange header as the proposal PDF, so
 * everything that leaves here looks like it came from the same firm.
 *
 * TWO RULES THAT MATTER TO AN UNDERWRITER:
 *
 *  1. An unanswered question is printed, blank. Omitting it would read as though
 *     it was never asked, and "not answered" and "answered no" are materially
 *     different on an application. The header states the completeness so nobody
 *     mistakes a partial form for a finished one.
 *  2. A question whose condition isn't met is left out entirely — it genuinely
 *     does not apply, and printing "if yes, explain: —" under a No is noise.
 */
import { jsPDF } from 'jspdf';
import type { FormAnswers, FormField, SupplementalForm } from '@commpass/shared';
import { completeness } from '@commpass/shared';

const NAVY = '#07385E';
const ORANGE = '#E3701E';
const MUTED = '#5F6B79';
const RULE = '#D8DCE1';

export interface SupplementalPdfMeta {
  businessName?: string;
  agencyName?: string;
  agentName?: string;
  /** Retrieval code, once responses are persisted. */
  reference?: string;
}

const isVisible = (f: FormField, a: FormAnswers): boolean =>
  !f.showIf || a[f.showIf.field] === f.showIf.equals;

/** Render an answer the way a person would write it, not the way it is stored. */
function display(field: FormField, value: FormAnswers[string]): string {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return ''; // tables are drawn separately
  if (field.type === 'currency') {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : String(value);
  }
  if (field.type === 'percent') return `${value}%`;
  return field.unit ? `${value} ${field.unit}` : String(value);
}

export function generateSupplementalPdf(
  form: SupplementalForm,
  answers: FormAnswers,
  meta: SupplementalPdfMeta = {},
): void {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 48;
  const BOTTOM = H - 54;
  let y = 0;

  const progress = completeness(form, answers);

  const header = (first: boolean): void => {
    doc.setFillColor(NAVY);
    doc.rect(0, 0, W, 8, 'F');
    y = 52;
    if (!first) return;

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(NAVY);
    doc.setFontSize(20);
    doc.text('Supplemental Application', M, y);

    doc.setFontSize(10);
    doc.setTextColor(ORANGE);
    doc.text('AGENTS ALLIANCE SERVICES', M, (y += 16));

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(MUTED);
    doc.setFontSize(9);
    doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), W - M, 52, {
      align: 'right',
    });
    if (meta.reference) doc.text(meta.reference, W - M, 66, { align: 'right' });

    y += 20;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(NAVY);
    doc.setFontSize(13);
    doc.text(meta.businessName || form.class, M, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(MUTED);
    const line = [
      `Class: ${form.class}`,
      meta.agencyName,
      meta.agentName,
      // Stated plainly: an underwriter reading a partial form should know it is
      // partial before they reach the gaps.
      `${progress.answered} of ${progress.total} questions answered`,
    ]
      .filter(Boolean)
      .join('   ·   ');
    doc.text(line, M, (y += 14));

    y += 10;
    doc.setDrawColor(RULE);
    doc.line(M, y, W - M, y);
    y += 20;
  };

  const room = (needed: number): void => {
    if (y + needed <= BOTTOM) return;
    doc.addPage();
    header(false);
  };

  header(true);

  for (const section of form.sections) {
    const fields = form.fields.filter((f) => f.section === section.id && isVisible(f, answers));
    if (!fields.length) continue;

    room(50);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(NAVY);
    doc.text(section.title, M, y);
    y += 6;
    doc.setDrawColor(RULE);
    doc.line(M, y, W - M, y);
    y += 16;

    for (const field of fields) {
      const label = doc.splitTextToSize(field.label, W - M * 2 - 150) as string[];
      const table = field.type === 'table' ? ((answers[field.id] as Record<string, unknown>[]) ?? []) : null;
      room(Math.max(label.length * 12, 16) + (table ? 14 * (table.length + 2) : 0) + 8);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor('#222');
      doc.text(label, M, y);

      if (!table) {
        const value = display(field, answers[field.id] ?? null);
        doc.setFont('helvetica', value ? 'bold' : 'normal');
        doc.setTextColor(value ? NAVY : '#B0B7BF');
        // An empty answer prints as a rule, so the gap is visibly a gap rather
        // than looking like the question was never on the form.
        doc.text(value || '—', W - M, y, { align: 'right' });
        y += Math.max(label.length * 12, 14) + 4;
      } else {
        y += label.length * 12 + 4;
        const cols = field.columns ?? [];
        const rows = field.rowLabels
          ? field.rowLabels.map((rl, i) => ({ label: rl, row: table[i] ?? {} }))
          : table.map((row, i) => ({ label: String(i + 1), row }));
        const colW = (W - M * 2 - 120) / Math.max(cols.length, 1);

        doc.setFontSize(8);
        doc.setTextColor(MUTED);
        cols.forEach((c, i) => doc.text(c.label, M + 120 + i * colW, y));
        y += 11;

        doc.setTextColor('#222');
        for (const { label: rowLabel, row } of rows) {
          room(14);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(MUTED);
          doc.text(String(rowLabel).slice(0, 28), M, y);
          doc.setTextColor('#222');
          cols.forEach((c, i) => {
            const v = row[c.id];
            doc.text(v == null || v === '' ? '—' : String(v).slice(0, 22), M + 120 + i * colW, y);
          });
          y += 12;
        }
        y += 6;
      }
    }
    y += 8;
  }

  // Footer on every page: what this is, and that it is not a bind request.
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(RULE);
    doc.line(M, H - 42, W - M, H - 42);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(MUTED);
    doc.text(
      `Prepared by Agents Alliance Services · questions transcribed from ${form.source.title}` +
        (form.source.carrier ? ` (${form.source.carrier})` : ''),
      M,
      H - 30,
    );
    doc.text(`Page ${p} of ${pages}`, W - M, H - 30, { align: 'right' });
  }

  const safe = (meta.businessName || form.class).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
  doc.save(`AAS-Supplemental-${safe}.pdf`);
}
